import keyBy from 'lodash/keyBy';

import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { getUnique } from 'utils/unique';

import { DEFAULT_RATE_LIMIT_PER_SEC } from './constants';
import { DataFeed, HttpHeader, RateLimitValue } from './types';
import { DataFeedColumn } from './hooks/useColumns';

/**
 * Validate an alias against defined rules
 */
export const ALIAS_REGEX = new RegExp('^[a-zA-Z_]+$');
export const validateAlias = (alias: string, aliasTest = ALIAS_REGEX): boolean => {
  // empty strings are allowed
  const aliasPasses = alias.length < 1 || aliasTest.test(alias);
  return aliasPasses;
};

/**
 * Given an array of headers, default them to include a unique id and return them as an object keyed by id
 *
 * Details: The header values need a stable ID so we can track additions / removals. Keying the object by ID allows for easier updates and provides a stable ordering of the headers so updates don't resort the inputs in the UI
 */
export const addDefaultId = (headers: HttpHeader[] = []) => {
  // allow 0 as a valid id
  const headersWithId = headers.map((header) => ({
    ...header,
    id: getUnique(12),
  }));

  return keyBy(headersWithId, 'id');
};

export const getRateLimitPerSec = (rateLimitPerMin: RateLimitValue) => {
  if (typeof rateLimitPerMin === 'number') {
    return Math.round(rateLimitPerMin / 60).toString();
  } else {
    return DEFAULT_RATE_LIMIT_PER_SEC.toString();
  }
};

/**
 * Filtering the rows by the active filter string requires concatenating the searchable text fields
 * on each row and then checking to see if they includes the search filter
 * @param {[]} columns - columns in the table with a .searchable key/value pair
 * @param {[]} rows - the rows in the table
 * @param {string} filter - the text to filter by
 */
export const filterRows = (columns: DataFeedColumn[] = [], rows: DataFeed[] = [], filter = '') =>
  rows.filter((row) => {
    const searchProps = columns.reduce((acc, col) => {
      if (!col.isSearchable) {
        // return early to skip non-searchable columns
        return acc;
      }

      // build up the string using the row data for each column
      // @ts-ignore
      return acc.concat(row[col.id]);
    }, '');

    // check if the accumulated values include the value we're filtering by
    return searchProps.toLowerCase().includes(filter.toLowerCase());
  });

/**
 * Sort objects by a value and direction
 * @param {[{}]} objectArray - an array of objects
 * @param {string} sortedBy  - the values to sort by
 * @param {string} sortDirection - ASCENDING or DESCENDING, DESCENDING is default
 */

export const sortRows = (
  objectArray: DataFeed[] = [],
  sortKey: keyof DataFeed,
  sortDirection: SORT_DIRECTIONS = SORT_DIRECTIONS.DESCENDING,
) => {
  const fallbackValue = ['id', 'createdAt', 'rateLimitPerMin'].includes(sortKey) ? 0 : '';
  return objectArray.sort((val1, val2) => {
    let formattedValue1 = val1[sortKey] || fallbackValue;
    let formattedValue2 = val2[sortKey] || fallbackValue;
    if (typeof formattedValue1 === 'string' && typeof formattedValue2 === 'string') {
      formattedValue1 = formattedValue1.toLowerCase();
      formattedValue2 = formattedValue2.toLowerCase();
    }
    if (sortDirection === SORT_DIRECTIONS.DESCENDING) {
      // @ts-ignore
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    // @ts-ignore
    return formattedValue2 > formattedValue1 ? -1 : 1;
  });
};
